import Skeleton from "react-loading-skeleton"

import { SvgIcon } from "@components/common"
import { getParticipantStatus } from "@utils/peopleUtils"
import { useTeeupPeopleStore } from "stores/teeupPeopleStore"

import ParticipantIcon from "../Content/Participants/ParticipantItem/ParticipantIcon"
import AttendanceModal from "../Modals/Attendance"
import { orderPeopleForDisplay } from "../peopleOrder"

import "./index.scss"

// QJ SPEC 2026-07-04 (definitive, supersedes the earlier mobile-STATUS_CHANGE-only sort):
// how many avatars show before the "+N" opener chip appears. The strip is ALSO horizontally
// scrollable past this point (both behaviors, not either/or) -- this constant only decides
// when the shortcut chip appears, matching mobile's own collapsed-row precedent
// (ParticipantsList/index.tsx maxUsersToRender = 5, minus 1 slot here since the organizer
// already gets its own separate labeled slot on this row).
const VISIBLE_BEFORE_OVERFLOW_CHIP = 4

// TEEUP-DETAIL-BODYLAYOUT 2026-07-03: rebuilt to Ale's exact People row (`coowe.html` tt()) --
// ORGANIZER avatar + label (spec: NEVER "Host", "Organizer" only) on the left, then the other
// participants' avatar stack + "N people", the Multitimezone selector entry point, and the
// "I'm going" attendance status control on the right -- ONE clean single row (same row Collapse
// renders its own chevron after). Clicking Multitimezone opens the row (same handler as the row
// itself) so the user lands on the already-functional expanded Timezone view (Content/Timezone,
// unchanged) -- a real navigation aid, not a decorative label.
// FIX 2026-07-03 (QJ-flagged regression): the attendance status control (AttendanceModal) used
// to live in SubTitleContent, rendered as a SIBLING row directly below this one along with a
// duplicate of the participant avatar stack -- producing a stray second line (duplicate avatar +
// the status control's green icon, which read as a stray arrow). Moved AttendanceModal here, into
// this single row, and removed the duplicate render from SubTitleContent (see that file).
const TitleContent = ({
  people,
  total,
  attending,
  isMultiTimezone,
  userId,
  isCollapsed,
}) => {
  const setCurrentParticipant = useTeeupPeopleStore(
    state => state.setCurrentParticipant
  )
  const setIsParticipantDetailsModalOpen = useTeeupPeopleStore(
    state => state.setIsParticipantDetailsModalOpen
  )
  const setIsTimeZoneSummaryModalOpen = useTeeupPeopleStore(
    state => state.setIsTimeZoneSummaryModalOpen
  )

  // Restores the original Time Zone Summary popup entry point (mobile 2018-2019 design): a
  // one-tap modal listing every participant's timezone/local time. stopPropagation so the
  // click doesn't also toggle the card's own Collapse (the row's default click behavior).
  const openTimeZoneSummary = e => {
    e.stopPropagation()
    setIsTimeZoneSummaryModalOpen(true)
  }

  if (!people || (!total && total !== 0) || (!attending && attending !== 0))
    return <Skeleton width={100} borderRadius={50} />

  // FIX 2026-07-04 (organizer crown parity, iPhone gold standard): the collapsed People-card
  // organizer avatar rendered with no `isOrganizer` prop, so ParticipantIcon's crown-2 badge
  // never showed here even though the expanded roster (Content/Participants/ParticipantItem)
  // marks the organizer correctly. Passing isOrganizer reuses that exact same crown asset.
  const organizer = people.find(p => p.isOrganiser)
  const currentUser = people.find(p => p.id === userId)

  // NOTE: `isCollapsed` (the store flag threaded down from TeeupPeople/index.jsx) is TRUE when
  // the card's full roster/detail body is OPEN (see ui/Collapse: `collapse-content-open` is only
  // applied when isCollapsed is true -- the prop name is inherited from mobile and is inverted
  // from its English meaning; documented at length in Content/Participants/index.jsx). Aliased
  // here for readability so the branch below reads naturally.
  const isExpanded = isCollapsed

  // PEOPLE-ROW-PILLS 2026-07-05 (QJ): order is now computed by the SAME shared function the
  // expanded per-person activity list uses (Content/Participants), so the two provably agree
  // (verification requirement: list order === strip order). The featured organizer keeps its
  // own separate labeled slot (existing web layout, unaffected) -- just excluded here from the
  // general strip by id; any CO-organizers still sort into the front of the strip below since
  // the organizer group leads overall.
  const others = organizer
    ? orderPeopleForDisplay(people).filter(p => p.id !== organizer.id)
    : orderPeopleForDisplay(people)

  // FIX 2026-07-04 (People-card avatar tap parity, iPhone participantDetails):
  // these avatars used to render with no onClick at all, so a tap just bubbled up to
  // the Collapse header's toggle (expand/collapse the People card) -- inert from the
  // user's point of view. Reuse the SAME zustand store + ParticipantDetails modal the
  // conversation avatar already opens (Content/Participants also wires this identically).
  const openParticipantDetails = (participant, e) => {
    e.stopPropagation()
    setCurrentParticipant(participant)
    setIsParticipantDetailsModalOpen(true)
  }

  return (
    <div
      className="people-title-content-container"
      data-testid="teeup-detail-people-row">
      <div className="people-title-left">
        {/* PEOPLE-ROW-PILLS 2026-07-05 (QJ): the written "Organizer" word is redundant with the
            yellow crown-2 badge ParticipantIcon already renders via `isOrganizer` -- the crown
            IS the organizer marker everywhere else (inbox cards, participant rows), so drop the
            text label here too and let the row read purely by the avatar badge. */}
        {organizer && (
          <span
            className="people-organizer people-avatar-clickable"
            data-testid="teeup-detail-organizer"
            title="Organizer"
            onClick={e => openParticipantDetails(organizer, e)}>
            <ParticipantIcon
              avatar={organizer.avatar}
              status={getParticipantStatus(organizer.status)}
              isOrganizer
              defaultAvatar={organizer.name || organizer.username}
              className="!size-6"
            />
          </span>
        )}

        {/* Spec ("People Card Collapsed", 5ba3f8cbdf4c43f4a20d3bc91beea892): while the card is
            COLLAPSED, show exactly 4 avatars max -- if there are more than 4, the 4th renders at
            50% opacity with a "+N" count overlaid on it (N = participants not shown). No
            drag-scroll here: 4 is a hard cap, nothing to scroll to. */}
        {/* People Card Detail-Groups spec (Notion d45923fee2264c559a5512601920958c, 2026-07-05,
            QJ-flagged defect 1 -- duplication): the collapsed overview ("keep the existing
            implementation, it's correct") stays exactly as it was -- 4-avatar capped stack, 4th
            faded with "+N". Once EXPANDED, the header row above the detail-groups stack no
            longer renders the full scrollable avatar strip / overflow chip / "Your Status"
            control -- those are now the Info group's roster (Content/Participants) and the My
            Status group (Content/YourStatus) respectively, each rendered EXACTLY ONCE below.
            Rendering them again here duplicated "Your Status + I'm going" and the whole avatar
            strip between the collapsed row and the expanded body (QJ screenshot defect 1). The
            header row when expanded keeps only the organizer + count + Multitimezone shortcut,
            none of which repeat content that appears again in the groups below. */}
        {!isExpanded && (
          <span
            className="people-avatar-stack people-avatar-stack-collapsed"
            data-testid="teeup-detail-people-avatar-strip-collapsed">
            {others.slice(0, VISIBLE_BEFORE_OVERFLOW_CHIP).map((p, index) => {
              const isLastVisible = index === VISIBLE_BEFORE_OVERFLOW_CHIP - 1
              const hiddenCount = others.length - VISIBLE_BEFORE_OVERFLOW_CHIP
              const isFadedWithOverflow = isLastVisible && hiddenCount > 0

              if (isFadedWithOverflow) {
                return (
                  <span
                    key={p.id}
                    className="people-avatar-overlay-wrap"
                    data-testid="teeup-detail-people-overflow-avatar"
                    data-participant-id={p.id}>
                    <ParticipantIcon
                      avatar={p.avatar}
                      status={getParticipantStatus(p.status)}
                      defaultAvatar={p.name || p.username}
                      className="!size-6 people-avatar-faded"
                    />
                    <span className="people-avatar-overlay-count">
                      +{hiddenCount}
                    </span>
                  </span>
                )
              }

              return (
                <span
                  key={p.id}
                  className="people-avatar-clickable"
                  data-participant-id={p.id}
                  onClick={e => openParticipantDetails(p, e)}>
                  <ParticipantIcon
                    avatar={p.avatar}
                    status={getParticipantStatus(p.status)}
                    defaultAvatar={p.name || p.username}
                    className="!size-6"
                  />
                </span>
              )
            })}
          </span>
        )}

        <span className="people-count">{total} people</span>
      </div>

      <div className="people-title-right">
        {/* Spec ("people card requirements (for Alessandra)", ecefe436ffc041b994b32ebf2b3273e1):
            the collapsed card differs single vs multi-TZ -- same-TZ TeeUps get NO timezone
            card/chip at all. This chip is the entry point to the expanded Timezone detail group
            (Content/Timezone), which itself only renders for multi-TZ, so gate the chip the
            same way instead of always showing it. Kept visible in BOTH collapsed and expanded
            header states -- it is a navigation shortcut, not a duplicate of the Time Zones
            group's own content (the group renders zone rows; this is just an entry point). */}
        {/* PEOPLE-ROW-PILLS 2026-07-05 (QJ): collapsed to a compact globe-only pill -- no
            written "Multitimezone" word, no down arrow. Symbol carries the meaning; the
            accessible name lives in title/aria-label. Click behavior (opens the Timezone
            Summary modal) is unchanged. */}
        {isMultiTimezone && (
          <button
            type="button"
            className="people-multitimezone people-multitimezone-clickable"
            data-testid="teeup-detail-multitimezone"
            title="Multitimezone"
            aria-label="Multitimezone"
            onClick={openTimeZoneSummary}>
            <SvgIcon name="planet" color="var(--gray4)" className="!shrink-0" />
          </button>
        )}

        {/* DEFECT 1 FIX: this "Your Status" pill duplicated Content/YourStatus (the My Status
            detail group) once the card was expanded -- only render it in the collapsed header,
            matching the ratified collapsed-overview spec ("Add Participants + Change Status
            buttons"). Expanded state's status control lives solely in the My Status group. */}
        {currentUser && !isExpanded && (
          <span
            className="people-my-status"
            data-testid="teeup-detail-my-status"
            onClick={e => e.stopPropagation()}>
            <AttendanceModal user={currentUser} />
          </span>
        )}
      </div>
    </div>
  )
}

export default TitleContent
